/**
 * clock — when to redraw the wait timers, and how far the device clock is from the server's.
 *
 * WHY THIS EXISTS (2026-10-30, plan "Clock & timers"). Every wait on the board is a number of minutes
 * ("Waiting 14 min") computed from stored timestamps — never counted up by a timer, so a laptop that sleeps
 * for an hour and wakes shows 60 minutes more at once, not the old number ticking on. Two things remain:
 *
 *   msToNextMinute(nowMs)   schedule the next redraw for the START of the next clock minute (a fixed 60 s
 *                           interval drifts and shows 14 min for up to 59 s after it is really 15). The page
 *                           also redraws at once on `visibilitychange` / wake — that wiring is the page's.
 *   serverOffsetMs(...)     a device clock can be minutes wrong; the board's timestamps are server-side, so
 *                           one wrong clock would shift every wait. The offset = server now − device now,
 *                           taken from the HTTP headers of a request the page already makes.
 *
 * ⛔ THE SERVER'S TIME IS `Date` + `Age`, NOT `Date`. /version.json (src/services/versionCheck.js) is served from
 * the CloudFront cache with the query string ignored: on 2026-10-30 prod answered `x-cache: Hit`, `age: 62679`
 * and a `Date` header 17 HOURS old. `Date` alone would have skewed every wait by hours (plan Phase 0, result 5).
 * `Date` is when the ORIGIN produced the object; `Age` is how many seconds it has sat in the cache since, so
 * Date + Age is the cache's "now" when it answered. (A fresh, uncached response has no `Age`: 0 is right.)
 *
 * PRECISION. `Date` has whole-second resolution and the request takes time, so the offset is good to about a
 * second. That is plenty: waits are shown in whole minutes. The device's moment of the response is taken as the
 * MIDPOINT of the request (start..end), the usual estimate when the server stamps mid-flight.
 *
 * Usage:  const offset = serverOffsetMs({ dateHeader, ageHeader, requestStartMs, responseEndMs });
 *         const nowMs = Date.now() + offset;          // the corrected "now" every wait is computed from
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

export const MS_PER_MINUTE = 60 * 1000;

/**
 * Milliseconds from `nowMs` to the next minute boundary: always 1..60000. Exactly ON a boundary it answers
 * 60000 (the NEXT one — a timer must never be told to fire in 0 ms and spin). A value that is not a finite
 * number answers 60000, so a bad clock still ticks.
 */
export function msToNextMinute(nowMs) {
  if (typeof nowMs !== 'number' || !Number.isFinite(nowMs)) return MS_PER_MINUTE;
  const intoMinute = ((nowMs % MS_PER_MINUTE) + MS_PER_MINUTE) % MS_PER_MINUTE;   // 0..59999.x, also for negative ms
  const wait = Math.ceil(MS_PER_MINUTE - intoMinute);                              // 1..60000
  return Math.min(MS_PER_MINUTE, Math.max(1, wait));
}

function finiteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Server now − device now, in ms (positive = the device clock is BEHIND the server).
 *
 * @param {{dateHeader: ?string, ageHeader: ?(string|number), requestStartMs: number, responseEndMs: number}} input
 *   dateHeader     the response's `Date` header (an HTTP date). Missing or unparsable ⇒ the whole answer is 0.
 *   ageHeader      the response's `Age` header, SECONDS (a string as headers give it, or a number). Missing,
 *                  negative or garbage ⇒ 0 seconds of age.
 *   requestStartMs Date.now() just before the request; responseEndMs just after the response arrived.
 *                  If only one is usable it stands in for the midpoint; if neither is, the answer is 0.
 * @returns {number} the offset in whole milliseconds; 0 whenever it cannot be worked out. Never NaN.
 */
export function serverOffsetMs(input) {
  const { dateHeader, ageHeader, requestStartMs, responseEndMs } = input && typeof input === 'object' ? input : {};
  if (typeof dateHeader !== 'string' || dateHeader.trim() === '') return 0;
  const dateMs = Date.parse(dateHeader);
  if (!Number.isFinite(dateMs)) return 0;

  const ageSeconds = finiteNumber(ageHeader);
  const age = ageSeconds !== null && ageSeconds > 0 ? ageSeconds : 0;

  const start = finiteNumber(requestStartMs);
  const end = finiteNumber(responseEndMs);
  let deviceMid;
  if (start !== null && end !== null) deviceMid = (start + end) / 2;
  else if (end !== null) deviceMid = end;
  else if (start !== null) deviceMid = start;
  else return 0;

  const offset = Math.round(dateMs + age * 1000 - deviceMid);
  return Number.isFinite(offset) ? offset : 0;
}

/**
 * The stale-cache guard. A server offset larger than this is not a drifting device clock, it is a response that
 * was served from a cache (or a device whose date is badly wrong): treating it as real would shift every wait on
 * the board by hours. Past it the page uses NO correction at all.
 */
export const MAX_PLAUSIBLE_OFFSET_MS = 6 * 60 * MS_PER_MINUTE;

/** `offset` when |offset| <= 6 hours, else 0. Never NaN. */
export function plausibleOffsetMs(offset) {
  if (typeof offset !== 'number' || !Number.isFinite(offset)) return 0;
  return Math.abs(offset) > MAX_PLAUSIBLE_OFFSET_MS ? 0 : offset;
}
